import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router-dom";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize2,
  RotateCw,
} from "lucide-react";
import logoImage from "../../assets/logo.png";
import "./videoPlayer.css";

// Icons
const RewindIcon = ({ className, size = 20 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const ForwardIcon = ({ className, size = 20 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
  </svg>
);

const LandscapeIcon = ({ className, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="7" y="3" width="10" height="18" rx="2" />
    <path d="M3 10a9 9 0 0 1 9-9" />
    <path d="M21 14a9 9 0 0 1-9 9" />
  </svg>
);

const HIDE_DELAY_PLAYING_MS = 2500; // when video is playing
const HIDE_DELAY_PAUSED_MS = 1500; // when paused (a bit quicker)

const VideoPlayer = ({ url, title, isPlayingSet, isLive = false }) => {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const wrapperRef = useRef(null);
  const hideTimer = useRef(null);
  const skipAnimationTimeout = useRef(null);
  const volumeControlRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(!!isPlayingSet);
  const [progress, setProgress] = useState(0); // 0..100 watched %
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [showSkipAnimation, setShowSkipAnimation] = useState("");
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const effectiveVolume = isMuted ? 0 : volume;
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);

  const [isProgressHovered, setIsProgressHovered] = useState(false);
  const [isLandscapeLocked, setIsLandscapeLocked] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0); // %
  const [hoverTime, setHoverTime] = useState(0);

  const [controlsVisible, setControlsVisible] = useState(true);

  const normalizedUrl = useMemo(
    () => (typeof url === "string" ? url.trim() : url),
    [url]
  );

  // controls show/hide
  const showControls = useCallback(() => {
    setControlsVisible(true);

    if (hideTimer.current) clearTimeout(hideTimer.current);

    // Always auto-hide, even when paused (YouTube-style)
    const delay = isPlaying ? HIDE_DELAY_PLAYING_MS : HIDE_DELAY_PAUSED_MS;

    hideTimer.current = setTimeout(() => setControlsVisible(false), delay);
  }, [isPlaying]);

  const hideControlsNow = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setControlsVisible(false);
  }, []);

  useEffect(() => {
    if (!ready) return;

    // Whenever play/pause or fullscreen changes,
    // show controls briefly, then auto-hide (YouTube-like)
    showControls();

    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [isPlaying, isFullscreen, ready, showControls]);

  useEffect(() => {
    function onFsChange() {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      const inFs = !!fsEl;
      setIsFullscreen(inFs);

      if (inFs) {
        setControlsVisible(true);
      } else {
        // leaving fullscreen → consider landscape lock off
        setIsLandscapeLocked(false);
      }
    }
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("mozfullscreenchange", onFsChange);
    document.addEventListener("MSFullscreenChange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("mozfullscreenchange", onFsChange);
      document.removeEventListener("MSFullscreenChange", onFsChange);
    };
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      if (window.screen.orientation.type.startsWith("landscape")) {
        // Go fullscreen automatically in landscape
        if (!document.fullscreenElement && containerRef.current) {
          containerRef.current.requestFullscreen?.().catch(() => {});
        }
      } else {
        // Exit fullscreen when back to portrait
        if (document.fullscreenElement) {
          document.exitFullscreen?.().catch(() => {});
        }
      }
    };

    window.screen.orientation?.addEventListener(
      "change",
      handleOrientationChange
    );

    return () => {
      window.screen.orientation?.removeEventListener(
        "change",
        handleOrientationChange
      );
    };
  }, []);

  useEffect(() => {
    const v = isMuted ? 0 : volume; // 🔥 if muted → use 0
    const percent = `${v * 100}%`;

    document.querySelectorAll(".volume-slider").forEach((el) => {
      el.style.setProperty("--volume-fill", percent);
    });
  }, [volume, isMuted]);

  // Touch devices fire a synthetic mouseenter on tap but often never fire
  // mouseleave, so the slider would stay open forever. Dismiss it whenever a
  // press lands outside the control.
  useEffect(() => {
    if (!isVolumeHovered) return;

    function onPointerDownOutside(e) {
      if (!volumeControlRef.current?.contains(e.target)) {
        setIsVolumeHovered(false);
      }
    }

    document.addEventListener("pointerdown", onPointerDownOutside, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDownOutside, true);
  }, [isVolumeHovered]);

  // The slider lives inside the controls overlay — don't leave it open
  // underneath a hidden overlay, or it reappears already-expanded.
  useEffect(() => {
    if (!controlsVisible) setIsVolumeHovered(false);
  }, [controlsVisible]);

  const handlePointerMove = () => showControls();
  const handlePointerLeave = () => hideControlsNow();
  const handleTap = () => showControls();

  // helpers
  const seekSeconds = (secs) => {
    const p = playerRef.current;
    if (!p) return;
    if (typeof p.seekTo === "function") p.seekTo(secs, "seconds");
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    setIsMuted(v === 0);
  };

  // replace your handleFullscreen with this cross-browser toggle
  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;

    // Prevent iOS Safari / Android Chrome from opening native fullscreen player
    // We'll rely on our own CSS `.is-fs` layout instead
    const isCurrentlyFS = document.fullscreenElement;

    if (!isCurrentlyFS) {
      // 👇 add a CSS class for your custom fullscreen layout
      el.classList.add("is-fs");
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
      el.classList.remove("is-fs");
    }
  };
  const handleLandscapeToggle = async () => {
    const el = containerRef.current;

    // No orientation API → just toggle fullscreen
    if (
      !window.screen ||
      !window.screen.orientation ||
      typeof window.screen.orientation.lock !== "function"
    ) {
      handleFullscreen();
      return;
    }

    try {
      if (!isLandscapeLocked) {
        // Must be fullscreen before locking on most browsers
        if (!document.fullscreenElement && el?.requestFullscreen) {
          await el.requestFullscreen();
        }

        // Different browsers accept 'landscape' or 'landscape-primary'
        await window.screen.orientation.lock("landscape");
        setIsLandscapeLocked(true);
      } else {
        window.screen.orientation.unlock?.();
        setIsLandscapeLocked(false);

        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      }
    } catch (err) {
      console.warn(
        "Orientation lock failed, falling back to fullscreen only",
        err
      );
      handleFullscreen();
    }
  };

  const handleProgress = ({ played, playedSeconds }) => {
    setProgress(played * 100);
    setCurrentTime(playedSeconds);
  };

  const handleSkip = (direction) => {
    if (!ready) return;
    const delta = direction === "forward" ? 10 : -10;
    const next = Math.min(Math.max(currentTime + delta, 0), duration);
    seekSeconds(next);
    setShowSkipAnimation(direction);
    if (skipAnimationTimeout.current)
      clearTimeout(skipAnimationTimeout.current);
    skipAnimationTimeout.current = setTimeout(
      () => setShowSkipAnimation(""),
      200
    );
    showControls();
  };

  const handleProgressBarClick = (e) => {
    if (!ready) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    seekSeconds(pct * duration);
    showControls();
  };

  const handleProgressHover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = (x / rect.width) * 100; // %
    setHoverPosition(pct);
    setHoverTime((pct / 100) * duration);
    setIsProgressHovered(true);
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0:00";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleUserPlayPause = () => {
    if (!ready) return;

    // If controls are hidden and we're already playing,
    // first tap just reveals controls (don't pause).
    if (!controlsVisible && isPlaying) {
      showControls();
      return;
    }

    // Otherwise, toggle play/pause as usual
    setIsPlaying((p) => !p);
    if (isMuted) setIsMuted(false);
    showControls();
  };
  const handleToggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  return (
    <div className="video-player">
      {/* <div className="video-header">
        <div className="video-header-title">{title}</div>
        <button className="video-header-close" onClick={() => navigate("/")}>
          ✕
        </button>
      </div> */}
      <div
        ref={containerRef}
        className={`app_video-player ${
          isPlaying ? "is-playing" : "is-paused"
        } ${isFullscreen ? "is-fs" : ""}`}
        onMouseMove={handlePointerMove}
        onMouseLeave={handlePointerLeave}
      >
        {/* Overlay header (title left, close right) */}

        <div className="skip-indicators">
          <div
            className={`skip-indicator ${
              showSkipAnimation === "backward" ? "show" : ""
            }`}
          >
            <RewindIcon />
          </div>
          <div
            className={`skip-indicator ${
              showSkipAnimation === "forward" ? "show" : ""
            }`}
          >
            <ForwardIcon />
          </div>
        </div>
        <div className="player-stage">
          <div className="video-wrapper" ref={wrapperRef}>
            <ReactPlayer
              ref={playerRef}
              url={normalizedUrl}
              width="100%"
              height="100%"
              playing={isPlaying}
              controls={false}
              volume={volume}
              muted={isMuted}
              playsinline
              onReady={() => setReady(true)}
              onProgress={handleProgress}
              onDuration={setDuration}
              config={{
                youtube: {
                  playerVars: {
                    playsinline: 1,
                    rel: 0,
                    modestbranding: 1,
                    disablekb: 1,
                    iv_load_policy: 3,
                    origin: window.location.origin,
                  },
                },
              }}
            />
            <div
              className="hover-overlay"
              onClick={handleUserPlayPause}
              style={{ pointerEvents: "auto" }}
              title={isPlaying ? "Pause" : "Play"}
            />
          </div>
        </div>
        <div className={`controls-overlay ${controlsVisible ? "visible" : ""}`}>
          <div className="controls-inner">
            <div className="progress-container">
              <div
                className="progress-bar"
                onClick={handleProgressBarClick}
                onMouseMove={handleProgressHover}
                onMouseLeave={() => {
                  // keep remainder always; only hide hover preview after a tiny delay
                  setTimeout(() => setIsProgressHovered(false), 200);
                }}
              >
                {/* Watched (orange line) */}
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="progress-remaining"
                  style={{ left: `${progress}%` }}
                />
                {/* Grey from watched → cursor (fills the gap) */}
                {isProgressHovered && hoverPosition > progress && (
                  <div
                    className="progress-hover-left"
                    style={{
                      left: `${progress}%`,
                      right: `${100 - hoverPosition}%`,
                    }}
                  />
                )}
                {/* Grey from max(cursor, watched) → end */}
                {isProgressHovered && (
                  <div
                    className="progress-remaining-hover"
                    style={{
                      left: `${Math.max(hoverPosition, progress)}%`,
                      right: 0,
                    }}
                  />
                )}
                {/* Round red handle */}
                <div
                  className="progress-handle"
                  style={{ left: `${progress}%` }}
                />
                {/* Time tooltip */}
                {isProgressHovered && (
                  <div
                    className="progress-hover-time"
                    style={{
                      left: `${hoverPosition}%`,
                      transform: "translateX(-50%)",
                    }}
                  >
                    {formatTime(hoverTime)}
                  </div>
                )}
              </div>
            </div>

            <div className="controls-row">
              <div className="controls-left">
                <button
                  className="control-button"
                  onClick={handleUserPlayPause}
                  disabled={!ready}
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>

                <button
                  className="control-button"
                  onClick={() => handleSkip("backward")}
                  disabled={!ready}
                >
                  <RewindIcon />
                </button>

                <button
                  className="control-button"
                  onClick={() => handleSkip("forward")}
                  disabled={!ready}
                >
                  <ForwardIcon />
                </button>

                <span className={`time-display ${isLive ? "live" : ""}`}>
                  {isLive ? (
                    <>
                      <span className="live-dot"></span>
                      <span className="live-text">LIVE</span>
                      <span className="live-sep">•</span>
                      {formatTime(currentTime)}
                    </>
                  ) : (
                    <>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </>
                  )}
                </span>
                <div
                  className="volume-control"
                  ref={volumeControlRef}
                  onMouseEnter={() => setIsVolumeHovered(true)}
                  onMouseLeave={() => setIsVolumeHovered(false)}
                >
                  <button
                    className="control-button"
                    onClick={() => setIsMuted((m) => !m)}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="volume-icon" />
                    ) : (
                      <Volume2 className="volume-icon" />
                    )}
                  </button>

                  <div
                    className={`volume-slider-container ${
                      isVolumeHovered ? "show" : ""
                    }`}
                  >
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={isMuted ? 0 : volume}
                      onChange={(e) => {
                        const newVol = parseFloat(e.target.value);
                        setVolume(newVol);
                        setIsMuted(newVol === 0);

                        // update orange fill
                        e.target.style.setProperty(
                          "--volume-fill",
                          `${newVol * 100}%`
                        );
                      }}
                      className="volume-slider"
                      onInput={showControls}
                    />
                  </div>
                </div>
              </div>

              <button
                className="control-button"
                onClick={handleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize2 /> : <Maximize />}
              </button>

              {/* NEW: Landscape button (mobile only) */}
              <span className="mobile-only">
                <button
                  className="control-button"
                  onClick={handleLandscapeToggle}
                  title={
                    isLandscapeLocked
                      ? "Exit landscape mode"
                      : "Rotate to landscape"
                  }
                >
                  <LandscapeIcon />
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
